import type { FirebaseOptions } from "firebase/app";
import { getStorage, ref, uploadBytes, getDownloadURL, type FirebaseStorage } from "firebase/storage";
import type { Storage as Interface } from "../../design/services";
import type { Config } from "../../design/types";
import { getFirebaseApp } from "../shared/firebase";

export class Storage implements Interface {
    private storage: FirebaseStorage;
    private stagingFiles: Map<string, File> = new Map();

    constructor(config: Config) {
        const options: FirebaseOptions = {
            apiKey: config.apiKey,
            authDomain: config.authDomain,
            projectId: config.projectId,
            storageBucket: config.storageBucket,
            messagingSenderId: config.messagingSenderId,
            appId: config.appId,
            measurementId: config.measurementId,
        };
        this.storage = getStorage(getFirebaseApp(options));
    }

    ["constructor"](config: Config): Interface {
        return new Storage(config);
    }

    async upload(content: File, staging?: boolean): Promise<string> {
        if (staging) {
            const url = URL.createObjectURL(content);
            this.stagingFiles.set(url, content);
            return url;
        }

        try {
            const id = crypto.randomUUID();
            const storageRef = ref(this.storage, `uploads/${id}/${content.name}`);
            await uploadBytes(storageRef, content);
            return getDownloadURL(storageRef);
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            console.error(`Storage upload failed for "${content.name}":`, err);
            throw new Error(`Failed to upload file "${content.name}": ${message}`);
        }
    }

    async download(url: string): Promise<File> {
        const file = this.stagingFiles.get(url);
        if (file) return file;

        // Fetch from Firebase Storage URL
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`File not found at URL: ${url}`);
            }
            const blob = await response.blob();
            const filename = url.split('/').pop()?.split('?')[0] || 'download';
            return new File([blob], filename, { type: blob.type });
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            console.error(`Storage download failed for "${url}":`, err);
            throw new Error(`Failed to download file: ${message}`);
        }
    }

    async commit(urls: string[]): Promise<Record<string, string>> {
        // Phase 1: Upload all files in parallel (staging untouched — safe to retry on failure)
        const uploads = urls
            .map(url => ({ url, file: this.stagingFiles.get(url) }))
            .filter((entry): entry is { url: string; file: File } => entry.file != null);

        const results = await Promise.all(uploads.map(async ({ url, file }) => {
            try {
                const id = crypto.randomUUID();
                const storageRef = ref(this.storage, `uploads/${id}/${file.name}`);
                await uploadBytes(storageRef, file);
                const permanentUrl = await getDownloadURL(storageRef);
                return { url, permanentUrl };
            } catch (err) {
                const message = err instanceof Error ? err.message : String(err);
                console.error(`Storage commit failed for "${file.name}":`, err);
                throw new Error(`Failed to upload file "${file.name}" during commit: ${message}`);
            }
        }));

        // Phase 2: Clean up staging (only after all uploads succeed)
        const mapping: Record<string, string> = {};
        for (const { url, permanentUrl } of results) {
            mapping[url] = permanentUrl;
            this.stagingFiles.delete(url);
            URL.revokeObjectURL(url);
        }
        return mapping;
    }

    async discard(urls: string[]): Promise<void> {
        for (const url of urls) {
            this.stagingFiles.delete(url);
            URL.revokeObjectURL(url);
        }
    }
}
